import React from 'react';
import { clsx } from '../../utils/clsx';

interface NetworkLogoProps {
  network: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showBackground?: boolean;
}

// Network logo SVG components
const MTNLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#FFCC00" />
    <text x="50" y="58" textAnchor="middle" fill="#000" fontSize="28" fontWeight="bold" fontFamily="Arial, sans-serif">
      MTN
    </text>
  </svg>
);

const AirtelTigoLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#0066CC" />
    <text x="50" y="45" textAnchor="middle" fill="#FFF" fontSize="16" fontWeight="bold" fontFamily="Arial, sans-serif">
      Airtel
    </text>
    <text x="50" y="65" textAnchor="middle" fill="#FFF" fontSize="16" fontWeight="bold" fontFamily="Arial, sans-serif">
      Tigo
    </text>
  </svg>
);

const TelecelLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="#C41E3A" />
    <text x="50" y="58" textAnchor="middle" fill="#FFF" fontSize="18" fontWeight="bold" fontFamily="Arial, sans-serif">
      Telecel
    </text>
  </svg>
);

const sizeClasses = {
  xs: 'w-5 h-5',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
  xl: 'w-16 h-16',
};

const backgroundClasses: Record<string, string> = {
  mtn: 'bg-yellow-100 dark:bg-yellow-900/30',
  airteltigo: 'bg-blue-100 dark:bg-blue-900/30',
  at: 'bg-blue-100 dark:bg-blue-900/30',
  telecel: 'bg-red-100 dark:bg-red-900/30',
  vodafone: 'bg-red-100 dark:bg-red-900/30',
};

const NetworkLogo: React.FC<NetworkLogoProps> = ({ 
  network, 
  size = 'md', 
  className = '',
  showBackground = false 
}) => {
  const networkLower = network?.toLowerCase() || '';
  const sizeClass = sizeClasses[size];
  
  const logoComponent = () => {
    switch (networkLower) {
      case 'mtn':
        return <MTNLogo className={sizeClass} />;
      case 'airteltigo':
      case 'at':
        return <AirtelTigoLogo className={sizeClass} />;
      case 'telecel':
      case 'vodafone':
        return <TelecelLogo className={sizeClass} />;
      default:
        return (
          <div className={clsx(
            sizeClass,
            'rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center'
          )}>
            <span className="text-gray-500 dark:text-gray-400 text-xs font-bold">
              {network?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
        );
    }
  };

  if (showBackground) {
    return (
      <div className={clsx(
        'rounded-xl flex items-center justify-center p-2',
        backgroundClasses[networkLower] || 'bg-gray-100 dark:bg-gray-800',
        className
      )}>
        {logoComponent()}
      </div>
    );
  }

  return (
    <div className={clsx('flex items-center justify-center', className)}>
      {logoComponent()}
    </div>
  );
};

export default NetworkLogo;
